import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Save01Icon } from '@/components/icons/Save01Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useDispositiveProvisionTemplatesQuery } from '@/modules/dispositiveProvisions/components/DispositiveProvisionTemplatesMenu/graphql/DispositiveProvisionTemplates.generated';
import { TemplatePresetDialog } from '@/modules/dispositiveProvisions/components/TemplatePresetDialog';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { mapProvisionToRecipient } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { DispositiveProvisionsTemplateSplitScreenModal } from '@/modules/dispositiveProvisions/DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal';
import {
  DispositiveProvisionTemplateDetails,
  NEW_TEMPLATE_SENTINEL,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.types';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionTemplateKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { getNodes } from '@/utils/graphqlUtils';

import { isPlaceholderRecipient } from './primaryClientDeathSection.utils';

interface PrimaryClientDeathSectionTemplatesMenuProps {
  deathEvent: PrimaryClientDeathEvent;
}

export function PrimaryClientDeathSectionTemplatesMenu({
  deathEvent,
}: PrimaryClientDeathSectionTemplatesMenuProps) {
  const { control, setValue } =
    useFormContext<DispositiveProvisionsFormV2Shape>();
  const { showFeedback } = useFeedback();
  const { householdId } = useDispositiveProvisionsContext();

  const [currentRecipients] = useWatch({
    name: [
      `${getDeathEventBasePath(deathEvent)}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>,
    ],
    control,
  });

  const [templateId] = useWatch({
    name: [
      `${getDeathEventBasePath(deathEvent)}.templateId` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>,
    ],
    control,
  });

  const shouldShowConvertToTemplateOption = !templateId;

  const [
    { isModalOpen: isTemplateModalOpen },
    { openModal: openTemplateModal, closeModal: closeTemplateModal },
  ] = useModalState();

  const [
    { isModalOpen: isPresetDialogOpen },
    { openModal: openPresetDialog, closeModal: closePresetDialog },
  ] = useModalState();

  const [selectedTemplate, setSelectedTemplate] = useState<
    (typeof templates)[0] | null
  >(null);
  const [
    { isModalOpen: isConfirmationModalOpen },
    { openModal: openConfirmationModal, closeModal: closeConfirmationModal },
  ] = useModalState();

  const [templateDetails, setTemplateDetails] =
    useState<DispositiveProvisionTemplateDetails | null>(null);

  const { data: templateData, loading: templatesLoading } =
    useDispositiveProvisionTemplatesQuery({
      variables: {
        where: {
          hasHouseholdWith: [
            {
              id: householdId,
            },
          ],
        },
      },
      skip: !householdId,
      fetchPolicy: 'cache-and-network',
    });

  const templates = useMemo(() => {
    return getNodes(templateData?.dispositiveProvisionTemplates);
  }, [templateData?.dispositiveProvisionTemplates]);

  const handlePresetSelect = useCallback(
    (details: DispositiveProvisionTemplateDetails) => {
      setTemplateDetails(details);
      closePresetDialog();
      openTemplateModal();
    },
    [closePresetDialog, openTemplateModal]
  );

  const handleCreateNewTemplate = useCallback(() => {
    openPresetDialog();
  }, [openPresetDialog]);

  const handleSaveAsTemplate = useCallback(() => {
    setTemplateDetails({
      templateId: NEW_TEMPLATE_SENTINEL,
      templateType: DispositiveProvisionTemplateKind.Default,
      recipients: currentRecipients,
    });
    openTemplateModal();
  }, [currentRecipients, openTemplateModal]);

  const templateIdPath = `${getDeathEventBasePath(
    deathEvent
  )}.templateId` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const recipientsPath = `${getDeathEventBasePath(
    deathEvent
  )}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const residuaryPaysAllTaxesPath = `${getDeathEventBasePath(
    deathEvent
  )}.residuaryPaysAllTaxes` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const handleApplyTemplate = useCallback(
    (template: (typeof templates)[0]) => {
      setValue(templateIdPath, template.id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(
        recipientsPath,
        getNodes(template.dispositiveProvisions).map((prov) =>
          mapProvisionToRecipient(prov)
        ),
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }
      );
      setValue(residuaryPaysAllTaxesPath, template.residuaryPaysAllTaxes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      showFeedback('Template applied', { variant: 'success' });
    },
    [
      setValue,
      templateIdPath,
      recipientsPath,
      residuaryPaysAllTaxesPath,
      showFeedback,
    ]
  );

  const handleTemplateClick = useCallback(
    (template: (typeof templates)[0]) => {
      const hasRecipients = Array.isArray(currentRecipients)
        ? currentRecipients.some(
            (recipient) => !isPlaceholderRecipient(recipient)
          )
        : false;

      if (hasRecipients) {
        setSelectedTemplate(template);
        openConfirmationModal();
      } else {
        handleApplyTemplate(template);
      }
    },
    [currentRecipients, handleApplyTemplate, openConfirmationModal]
  );

  const onCloseEditTemplateModal = useCallback(
    (
      updatedTemplateId?: string,
      updatedRecipients?: DispositiveProvisionsFormRecipient[],
      isDeleted?: boolean
    ) => {
      if (isDeleted) {
        setValue(templateIdPath, null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(recipientsPath, [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        closeTemplateModal();
        return;
      }

      if (!updatedTemplateId || !updatedRecipients?.length) {
        closeTemplateModal();
        return;
      }

      setValue(templateIdPath, updatedTemplateId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue(recipientsPath, updatedRecipients, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      showFeedback('Template saved', { variant: 'success' });

      closeTemplateModal();
    },
    [closeTemplateModal, setValue, templateIdPath, recipientsPath, showFeedback]
  );

  return (
    <>
      {isTemplateModalOpen && templateDetails && (
        <DispositiveProvisionsTemplateSplitScreenModal
          isOpen={isTemplateModalOpen}
          onClose={onCloseEditTemplateModal}
          templateDetails={templateDetails}
          isFromEditingDispositions
        />
      )}
      {isPresetDialogOpen && (
        <TemplatePresetDialog
          isOpen={isPresetDialogOpen}
          onClose={closePresetDialog}
          onSelect={handlePresetSelect}
        />
      )}
      {isConfirmationModalOpen && selectedTemplate && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          heading="Apply template"
          cancelText="Cancel"
          confirmText="Apply"
          onClose={(result) => {
            if (result === 'confirm' && selectedTemplate) {
              handleApplyTemplate(selectedTemplate);
            }
            closeConfirmationModal();
            setSelectedTemplate(null);
          }}
        >
          Current dispositions will be replaced with those from the selected
          template. Are you sure you would like to continue?
        </ConfirmationModal>
      )}
      <ButtonWithPopover
        contentBoxSx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        size="xs"
        variant="secondary"
        popperVariant="menuBelow"
        label="Use template"
        sx={{ whiteSpace: 'nowrap' }}
      >
        <MenuItem
          key="create-new-template"
          icon={<PlusIcon size={16} color={COLORS.NAVY['400']} />}
          label="Create disposition template"
          onClick={handleCreateNewTemplate}
        />
        {shouldShowConvertToTemplateOption && (
          <MenuItem
            key="save-as-template"
            icon={<Save01Icon size={16} color={COLORS.NAVY['400']} />}
            label="Save dispositions as template"
            onClick={handleSaveAsTemplate}
          />
        )}
        {templates.length > 0 && (
          <SelectItemGroupLabel label="Apply disposition template" />
        )}
        {templates.map((template) => (
          <MenuItem
            key={template.id}
            label={template.displayName}
            disabled={templatesLoading}
            onClick={() => handleTemplateClick(template)}
          />
        ))}
      </ButtonWithPopover>
    </>
  );
}
